<template>
  <!-- Pegman: drag onto the map for Street View; a plain click toggles the coverage preview. -->
  <span class="street-view-anchor">
  <button
    class="map-btn street-view-btn"
    :class="{ active: coverageVisible || dragging }"
    :aria-label="$t('components.map_controls.street_view')"
    :title="$t('components.map_controls.street_view_hint')"
    @pointerdown="onPointerDown"
    @keydown.enter.prevent="onKeyActivate"
    @keydown.space.prevent="onKeyActivate"
  >
    <svg class="pegman" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="5" r="3.2" />
      <path d="M8.4 9.6h7.2a1.6 1.6 0 0 1 1.6 1.6v4.6h-2.1V22h-6.2v-6.2H6.8v-4.6a1.6 1.6 0 0 1 1.6-1.6z" />
    </svg>
  </button>

    <!-- One-off launch hint for existing users. -->
    <div v-if="hintVisible" class="street-view-hint" role="status">
      <span>{{ $t('components.map_controls.street_view_new_hint') }}</span>
      <button class="street-view-hint-close" :aria-label="$t('components.map_controls.not_now')" @click="hintVisible = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </span>

  <Teleport to="body">
    <!-- Follows the pointer while dragging; feet sit on the drop point. -->
    <div v-if="dragging" class="street-view-ghost" :style="ghostStyle" aria-hidden="true">
      <svg class="pegman" viewBox="0 0 24 24">
        <circle cx="12" cy="5" r="3.2" />
        <path d="M8.4 9.6h7.2a1.6 1.6 0 0 1 1.6 1.6v4.6h-2.1V22h-6.2v-6.2H6.8v-4.6a1.6 1.6 0 0 1 1.6-1.6z" />
      </svg>
    </div>

    <div v-if="message" class="street-view-message" role="status">{{ message }}</div>

    <!-- PRO+ gate: shown instead of starting a drag for anyone without PRO+. -->
    <div v-if="paywallOpen" class="street-view-paywall-backdrop" @click.self="closePaywall">
      <div class="street-view-paywall" role="dialog" aria-modal="true" aria-labelledby="street-view-paywall-title">
        <svg class="pegman street-view-paywall-icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="5" r="3.2" />
          <path d="M8.4 9.6h7.2a1.6 1.6 0 0 1 1.6 1.6v4.6h-2.1V22h-6.2v-6.2H6.8v-4.6a1.6 1.6 0 0 1 1.6-1.6z" />
        </svg>
        <h2 id="street-view-paywall-title">{{ $t('components.map_controls.street_view_pro_plus_title') }}</h2>
        <p>{{ $t('components.map_controls.street_view_pro_plus_body') }}</p>
        <div class="street-view-paywall-actions">
          <button class="btn btn-outline-light" @click="closePaywall">{{ $t('components.map_controls.not_now') }}</button>
          <a class="btn btn-info fw-bold" :href="upgradeUrl" @click="onSeePricing">{{ $t('components.map_controls.see_pricing') }}</a>
        </div>
      </div>
    </div>

    <div v-if="viewerOpen" class="street-view-viewer" role="dialog" :aria-label="$t('components.map_controls.street_view')">
      <div ref="panorama" class="street-view-panorama"></div>
      <div v-if="viewerLoading" class="street-view-loading">
        <span class="spinner-border text-light" role="status"></span>
      </div>
      <button class="street-view-close" :aria-label="$t('components.map_controls.close_street_view')" @click="closeViewer">
        <span class="material-icons">close</span>
      </button>
    </div>
  </Teleport>
</template>

<script>
import {
  dropPointOnMap,
  dropSearchRadiusMeters,
  hasPassedDragThreshold,
  headingFromBearing,
  shouldShowStreetViewHint,
  STREET_VIEW_PREVIEW_STORAGE_KEY,
  STREET_VIEW_UPGRADE_URL,
  streetViewAccess,
  streetViewHintStorageKey,
} from '../../utils/helpers/streetView';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import {
  findPanorama,
  hideCoverage,
  loadGoogleMaps,
  showCoverage,
} from '../../utils/streetViewGoogle';
import { reportHandledError } from '../../utils/bugsnag';

const MESSAGE_MS = 3000;
const HINT_MS = 12000;

function previewUsed() {
  try { return window.localStorage.getItem(STREET_VIEW_PREVIEW_STORAGE_KEY) === '1'; } catch { return false; }
}

function markPreviewUsed() {
  try { window.localStorage.setItem(STREET_VIEW_PREVIEW_STORAGE_KEY, '1'); } catch { /* private mode: another preview, harmless */ }
}

function hintSeen(key) {
  try { return window.localStorage.getItem(key) === '1'; } catch { return true; }
}

function markHintSeen(key) {
  try { window.localStorage.setItem(key, '1'); } catch { /* private mode: it may show again, harmless */ }
}

export default {
  name: 'StreetViewControl',
  data() {
    return {
      pressStart: null,
      dragging: false,
      pointer: { x: 0, y: 0 },
      coverageVisible: false,
      viewerOpen: false,
      viewerLoading: false,
      message: '',
      messageTimeoutId: null,
      isProPlus: null,
      paywallOpen: false,
      upgradeUrl: STREET_VIEW_UPGRADE_URL,
      hintVisible: false,
      /* 'allowed' (PRO+) or 'preview' (one free look) for the press in progress. */
      pressMode: null,
      hintTimeoutId: null,
    };
  },
  computed: {
    ghostStyle() {
      return { left: `${this.pointer.x}px`, top: `${this.pointer.y}px` };
    },
  },
  mounted() {
    this.maybeShowHint();
    // Asked up front so the first press can start dragging straight away.
    this.entitlement = RevenueCatWrapper.isProPlus().then((value) => {
      this.isProPlus = value;
      return value;
    });
  },
  beforeUnmount() {
    this.removePressListeners();
    document.removeEventListener('keydown', this.onViewerKeydown);
    document.removeEventListener('keydown', this.onPaywallKeydown);
    window.clearTimeout(this.messageTimeoutId);
    window.clearTimeout(this.hintTimeoutId);
  },
  methods: {
    maybeShowHint() {
      const user = window.user;
      if (!user?.firebase_user_id) return;
      const key = streetViewHintStorageKey(user.firebase_user_id);
      if (!shouldShowStreetViewHint(user, hintSeen(key))) return;
      // Marked seen on display: once means once, whether or not they act on it.
      markHintSeen(key);
      this.hintVisible = true;
      this.hintTimeoutId = window.setTimeout(() => { this.hintVisible = false; }, HINT_MS);
      window.mixpanel?.track('Street View Hint Shown');
    },
    /** True when the user may use Street View; otherwise opens the PRO+ dialog. */
    /** 'allowed' or 'preview' when the press may go ahead; otherwise opens the PRO+ dialog and returns null. */
    async resolveAccess() {
      let access = streetViewAccess(this.isProPlus, previewUsed());
      if (access === 'pending') access = streetViewAccess(await this.entitlement, previewUsed());
      if (access === 'allowed' || access === 'preview') return access;
      this.openPaywall();
      return null;
    },
    async onKeyActivate() {
      // The preview is a drag; keyboard users without PRO+ go straight to the dialog.
      if (await this.resolveAccess() === 'allowed') this.toggleCoverage();
      else if (!this.paywallOpen) this.openPaywall();
    },
    async onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      this.hintVisible = false;
      this.pressMode = null;
      this.pressMode = await this.resolveAccess();
      if (!this.pressMode) return;
      this.pressStart = { x: e.clientX, y: e.clientY };
      this.pointer = { x: e.clientX, y: e.clientY };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.cancelDrag);
      window.addEventListener('keydown', this.onDragKeydown);
      // Warm the Google script up while the user is still deciding where to drop.
      loadGoogleMaps().catch(() => {});
    },
    onPointerMove(e) {
      this.pointer = { x: e.clientX, y: e.clientY };
      if (!this.dragging && hasPassedDragThreshold(this.pressStart, this.pointer)) {
        this.dragging = true;
        // The free look is spent the moment the lines appear, dropped or not.
        if (this.pressMode === 'preview') markPreviewUsed();
        showCoverage(window.map);
      }
    },
    async onPointerUp(e) {
      const wasDragging = this.dragging;
      this.removePressListeners();
      this.dragging = false;

      if (this.pressMode === 'preview') {
        // Preview: the drop (or a plain click) is where PRO+ comes in. No panorama is loaded.
        hideCoverage(window.map);
        this.openPaywall();
        return;
      }
      if (!wasDragging) {
        this.toggleCoverage();
        return;
      }
      if (!this.coverageVisible) hideCoverage(window.map);
      await this.dropAt({ x: e.clientX, y: e.clientY });
    },
    cancelDrag() {
      this.removePressListeners();
      if (this.dragging && !this.coverageVisible) hideCoverage(window.map);
      this.dragging = false;
    },
    onDragKeydown(e) {
      if (e.key === 'Escape') this.cancelDrag();
    },
    removePressListeners() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.cancelDrag);
      window.removeEventListener('keydown', this.onDragKeydown);
      this.pressStart = null;
    },
    toggleCoverage() {
      this.coverageVisible = !this.coverageVisible;
      if (this.coverageVisible) {
        showCoverage(window.map);
        this.flash(this.$t('components.map_controls.street_view_hint'));
      } else {
        hideCoverage(window.map);
      }
    },
    async dropAt(client) {
      const map = window.map;
      if (!map) return;
      const point = dropPointOnMap(client, map.getCanvas().getBoundingClientRect());
      if (!point) return;

      const { lat, lng } = map.unproject([point.x, point.y]);
      try {
        const found = await findPanorama(lat, lng, dropSearchRadiusMeters(lat, map.getZoom()));
        if (!found) {
          this.flash(this.$t('components.map_controls.no_street_view_here'));
          window.mixpanel?.track('Street View No Imagery', { zoom: map.getZoom() });
          return;
        }
        await this.openViewer(found, map.getBearing());
        window.mixpanel?.track('Street View Opened', { source: 'web_pegman' });
      } catch (error) {
        reportHandledError(error, { feature: 'street_view_drop', lat, lng });
        this.flash(this.$t('components.map_controls.street_view_unavailable'));
      }
    },
    async openViewer(found, bearing) {
      this.viewerOpen = true;
      this.viewerLoading = true;
      document.addEventListener('keydown', this.onViewerKeydown);
      await this.$nextTick();

      const maps = await loadGoogleMaps();
      const panorama = new maps.StreetViewPanorama(this.$refs.panorama, {
        pano: found.panoId,
        pov: { heading: headingFromBearing(bearing), pitch: 0 },
        addressControl: true,
        fullscreenControl: false,
        motionTracking: false,
        motionTrackingControl: false,
      });
      panorama.addListener('status_changed', () => { this.viewerLoading = false; });
    },
    closeViewer() {
      this.viewerOpen = false;
      this.viewerLoading = false;
      document.removeEventListener('keydown', this.onViewerKeydown);
    },
    onViewerKeydown(e) {
      if (e.key === 'Escape') this.closeViewer();
    },
    openPaywall() {
      this.paywallOpen = true;
      document.addEventListener('keydown', this.onPaywallKeydown);
      window.mixpanel?.track('Street View Paywall Shown', { after_preview: this.pressMode === 'preview' });
    },
    closePaywall() {
      this.paywallOpen = false;
      document.removeEventListener('keydown', this.onPaywallKeydown);
    },
    onPaywallKeydown(e) {
      if (e.key === 'Escape') this.closePaywall();
    },
    onSeePricing() {
      window.mixpanel?.track('Street View Paywall Pricing Clicked');
    },
    flash(text) {
      this.message = text;
      window.clearTimeout(this.messageTimeoutId);
      this.messageTimeoutId = window.setTimeout(() => { this.message = ''; }, MESSAGE_MS);
    },
  },
};
</script>

<style scoped>
.street-view-anchor {
  position: relative;
  display: inline-flex;
}
.street-view-hint {
  position: absolute;
  top: calc(100% + 10px);
  right: -8px;
  width: max-content;
  max-width: 240px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 6px 8px 12px;
  border-radius: 8px;
  background: #ffc72c;
  color: #111;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  pointer-events: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}
/* Arrow pointing up at the pegman. */
.street-view-hint::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 18px;
  border: 6px solid transparent;
  border-top: 0;
  border-bottom-color: #ffc72c;
}
.street-view-hint-close {
  border: none;
  background: none;
  padding: 0;
  color: #111;
  line-height: 1;
}
.street-view-hint-close .material-icons {
  font-size: 16px;
}
.street-view-btn {
  touch-action: none;
  cursor: grab;
}
.pegman {
  width: 20px;
  height: 20px;
  fill: #ffc72c;
}
</style>

<style>
/* Teleported to <body>, so these can't be scoped. */
.street-view-ghost {
  position: fixed;
  z-index: 2100;
  pointer-events: none;
  transform: translate(-50%, -100%);
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.5));
}
.street-view-ghost .pegman {
  width: 36px;
  height: 36px;
  fill: #ffc72c;
}
.street-view-message {
  position: fixed;
  left: 50%;
  bottom: 32px;
  transform: translateX(-50%);
  z-index: 2100;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(17, 17, 17, 0.9);
  color: #fff;
  font-size: 14px;
  pointer-events: none;
}
.street-view-paywall-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.55);
}
.street-view-paywall {
  width: 100%;
  max-width: 380px;
  padding: 24px;
  border-radius: 14px;
  background: #1a1a1a;
  color: #fff;
  text-align: center;
}
.street-view-paywall h2 {
  margin: 8px 0;
  font-size: 20px;
  font-weight: 700;
}
.street-view-paywall p {
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.8);
}
.street-view-paywall .street-view-paywall-icon {
  width: 44px;
  height: 44px;
  fill: #ffc72c;
}
.street-view-paywall-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.street-view-viewer {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: #000;
}
.street-view-panorama {
  width: 100%;
  height: 100%;
}
.street-view-loading {
  position: absolute;
  z-index: 5;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.street-view-close {
  position: absolute;
  /* Google stacks its own panorama controls above plain siblings. */
  z-index: 10;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(17, 17, 17, 0.85);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
